import type { ImportJob, ImportStatus, OwnerReport } from "../../api/imports";

/** Where the wizard stands. Always derived from the server's job, so a reload lands on the same step. */
export type Stage = "upload" | "mapping" | "check" | "review" | "rehearse" | "signoff" | "cutover" | "done" | "cancelled";

export const STEPS = [
  { key: "upload", label: "Sheets" },
  { key: "mapping", label: "Columns" },
  { key: "check", label: "Check" },
  { key: "rehearse", label: "Rehearse" },
  { key: "signoff", label: "Sign off" },
  { key: "cutover", label: "Cut over" },
] as const;

export function stageOf(job: Pick<ImportJob, "status" | "files" | "mappedFileIds">): Stage {
  switch (job.status) {
    case "draft":
      if (job.files.length === 0) return "upload";
      if (job.files.some((f) => !job.mappedFileIds.includes(f.id))) return "mapping";
      return "check";
    case "validated": return "review";
    case "dry_run_failed": return "rehearse";
    case "dry_run_passed": return "signoff";
    case "approved":
    case "cutting_over":
    case "failed": return "cutover";
    case "done": return "done";
    case "cancelled": return "cancelled";
  }
}

/** Index into STEPS that a stage belongs to (the stepper highlights it). */
export const stepIndex = (stage: Stage): number =>
  ({ upload: 0, mapping: 1, check: 2, review: 2, rehearse: 3, signoff: 4, cutover: 5, done: 6, cancelled: -1 })[stage];

/** Inputs can change (and return the job to draft) until cutover begins. */
export const EDITABLE: readonly ImportStatus[] = ["draft", "validated", "dry_run_passed", "dry_run_failed", "approved"];
export const isEditable = (s: ImportStatus) => EDITABLE.includes(s);
/** Cancel is allowed at any step before cutover begins (the server refuses cutting_over, failed and done). */
export const canCancel = (s: ImportStatus) => isEditable(s);

/** What stops the dry run: unmapped names, a blocked plan, or errors under `abort`. */
export function dryRunBlockers(r: OwnerReport): string[] {
  const out: string[] = [];
  if (r.unmappedPlanNames.length > 0) out.push(`${r.unmappedPlanNames.length} plan name(s) are not matched to a plan in your catalogue.`);
  if (r.unmappedRoleNames.length > 0) out.push(`${r.unmappedRoleNames.length} role name(s) are not matched to a role.`);
  if (r.blocked) out.push(r.blockedReason ?? "The plan is blocked.");
  else if (!r.canProceed) out.push("Fix the errors below, or upload a corrected sheet.");
  return out;
}

const OPERATION_LABELS: Record<string, string> = {
  create_staff: "Staff to add", merge_staff: "Staff already in Helmr (filled in, never overwritten)",
  create_member: "Members to add", merge_member: "Members already in Helmr (filled in, never overwritten)",
  create_membership: "Membership plans to add", create_pt_pack: "PT packs to add", create_due: "Dues to add",
  map_device_user: "Fingerprint ids to link", create_lead: "Leads to add",
};
export const operationLabel = (type: string) => OPERATION_LABELS[type] ?? type;
export const OPERATION_ORDER = ["create_staff", "merge_staff", "create_member", "merge_member", "create_membership", "create_pt_pack", "create_due", "map_device_user", "create_lead"];
